import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';
import supabase from '../lib/supabase';

const useProjectStore = create(
  persist(
    (set, get) => ({
      projects: [],
      currentProject: null,
      prospects: [],
      isLoading: false,
      error: null,

      // Fetch projects from Supabase
      fetchProjects: async () => {
        set({ isLoading: true, error: null });
        try {
          console.log('Fetching projects from Supabase...');
          const { data: projectsData, error: projectsError } = await supabase
            .from('projects_vixea_a7b2c3d4')
            .select('*')
            .order('created_at', { ascending: false });

          if (projectsError) {
            throw projectsError;
          }

          console.log('Projects fetched:', projectsData);

          // Fetch questions for each project
          const projectsWithQuestions = await Promise.all(
            projectsData.map(async (project) => {
              const { data: questionsData, error: questionsError } = await supabase
                .from('questions_vixea_a7b2c3d4')
                .select('*')
                .eq('project_id', project.id)
                .order('order_num', { ascending: true });

              if (questionsError) {
                console.error('Error fetching questions for project:', project.id, questionsError);
                return {
                  id: project.id,
                  title: project.title,
                  company: project.company,
                  description: project.description || '',
                  requirements: project.requirements || '',
                  welcomeMessage: project.welcome_message || '',
                  farewellMessage: project.farewell_message || '',
                  thinkingTime: project.thinking_time || 30,
                  responseTime: project.response_time || 60,
                  allowRetake: project.allow_retake || false,
                  responseType: project.response_type || 'video',
                  requireCV: project.require_cv || false, // Cambiado a false por defecto
                  vixeaId: project.vixea_id || Math.floor(Math.random() * 900000) + 100000,
                  status: project.status || 'active',
                  createdAt: project.created_at,
                  responses: project.responses || 0,
                  questions: []
                };
              }

              console.log(`Questions for project ${project.id}:`, questionsData);

              return {
                id: project.id,
                title: project.title,
                company: project.company,
                description: project.description || '',
                requirements: project.requirements || '',
                welcomeMessage: project.welcome_message || '',
                farewellMessage: project.farewell_message || '',
                thinkingTime: project.thinking_time || 30,
                responseTime: project.response_time || 60,
                allowRetake: project.allow_retake || false,
                responseType: project.response_type || 'video',
                requireCV: project.require_cv || false, // Cambiado a false por defecto
                vixeaId: project.vixea_id || Math.floor(Math.random() * 900000) + 100000,
                status: project.status || 'active',
                createdAt: project.created_at,
                responses: project.responses || 0,
                questions: questionsData?.map(q => ({
                  id: q.id,
                  text: q.text,
                  type: q.type || 'video',
                  thinkingTime: q.thinking_time || project.thinking_time,
                  responseTime: q.response_time || project.response_time
                })) || []
              };
            })
          );

          console.log('Projects with questions:', projectsWithQuestions);
          set({ projects: projectsWithQuestions, isLoading: false });
        } catch (error) {
          console.error('Error in fetchProjects:', error);
          set({ error: error.message, isLoading: false });
        }
      },

      // Add new project
      addProject: async (projectData) => {
        set({ isLoading: true, error: null });
        try {
          console.log('Adding new project:', projectData);
          const vixeaId = Math.floor(Math.random() * 900000) + 100000;

          // Insert project into Supabase
          const { data: project, error: projectError } = await supabase
            .from('projects_vixea_a7b2c3d4')
            .insert({
              title: projectData.title,
              company: projectData.company,
              description: projectData.description,
              requirements: projectData.requirements,
              welcome_message: projectData.welcomeMessage,
              farewell_message: projectData.farewellMessage,
              thinking_time: projectData.thinkingTime,
              response_time: projectData.responseTime,
              allow_retake: projectData.allowRetake,
              response_type: projectData.responseType,
              require_cv: projectData.requireCV,
              vixea_id: vixeaId.toString(),
              status: 'active'
            })
            .select()
            .single();

          if (projectError) throw projectError;

          console.log('Project inserted:', project);

          // Insert questions
          let questions = [];
          if (projectData.questions && projectData.questions.length > 0) {
            const questionsToInsert = projectData.questions.map((q, index) => ({
              project_id: project.id,
              text: q.text,
              type: q.type || 'video',
              thinking_time: q.thinkingTime || projectData.thinkingTime,
              response_time: q.responseTime || projectData.responseTime,
              order_num: index + 1
            }));

            const { data: questionsData, error: questionsError } = await supabase
              .from('questions_vixea_a7b2c3d4')
              .insert(questionsToInsert)
              .select();

            if (questionsError) throw questionsError;

            console.log('Questions inserted:', questionsData);
            questions = questionsData.map(q => ({
              id: q.id,
              text: q.text,
              type: q.type || 'video',
              thinkingTime: q.thinking_time || project.thinking_time,
              responseTime: q.response_time || project.response_time
            }));
          }

          // Create complete project object
          const newProject = {
            id: project.id,
            title: project.title,
            company: project.company,
            description: project.description || '',
            requirements: project.requirements || '',
            welcomeMessage: project.welcome_message || '',
            farewellMessage: project.farewell_message || '',
            thinkingTime: project.thinking_time || 30,
            responseTime: project.response_time || 60,
            allowRetake: project.allow_retake || false,
            responseType: project.response_type || 'video',
            requireCV: project.require_cv || false,
            vixeaId: project.vixea_id,
            status: project.status || 'active',
            createdAt: project.created_at,
            responses: project.responses || 0,
            questions: questions
          };

          // Update local state
          set(state => ({
            projects: [newProject, ...state.projects],
            isLoading: false,
            error: null
          }));

          return newProject;
        } catch (error) {
          console.error('Error in addProject:', error);
          set({ error: error.message, isLoading: false });
          throw error;
        }
      },

      getProjectById: (id) => {
        try {
          console.log('Getting project by ID:', id);
          const project = get().projects.find(p => p.id === id);
          console.log('Found project:', project);
          return project;
        } catch (error) {
          console.error('Error in getProjectById:', error);
          return null;
        }
      },

      // Add prospect
      addProspect: async (prospectData) => {
        try {
          console.log('Adding prospect:', prospectData);
          
          const { data: prospect, error } = await supabase
            .from('prospects_vixea_a7b2c3d4')
            .insert({
              project_id: prospectData.projectId,
              name: prospectData.name,
              last_name: prospectData.lastName,
              email: prospectData.email, // Agregado el email
              phone: prospectData.phone,
              cv: prospectData.cv,
              responses: prospectData.responses,
              ai_score: Math.floor(Math.random() * 5) + 1
            })
            .select()
            .single();

          if (error) throw error;

          // Increment project response count
          await supabase.rpc('increment_responses', { 
            project_id_param: prospectData.projectId 
          });

          // Update local state
          const newProspect = {
            id: prospect.id,
            projectId: prospect.project_id,
            name: prospect.name,
            lastName: prospect.last_name,
            email: prospect.email, // Agregado el email
            phone: prospect.phone,
            cv: prospect.cv,
            responses: prospect.responses,
            createdAt: prospect.created_at,
            status: prospect.status,
            aiScore: prospect.ai_score
          };

          set(state => ({
            prospects: [...state.prospects, newProspect]
          }));

          // Update project response count in local state
          set(state => ({
            projects: state.projects.map(p => 
              p.id === prospectData.projectId 
                ? { ...p, responses: (p.responses || 0) + 1 }
                : p
            )
          }));

          return newProspect;
        } catch (error) {
          console.error('Error adding prospect:', error);
          throw error;
        }
      },

      updateProspectStatus: (prospectId, status) => {
        set(state => ({
          prospects: state.prospects.map(prospect =>
            prospect.id === prospectId ? { ...prospect, status } : prospect
          )
        }));
      }
    }),
    {
      name: 'vixea-projects-storage',
      partialize: (state) => ({
        projects: state.projects,
        prospects: state.prospects
      }),
      version: 1
    }
  )
);

export { useProjectStore };